import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { X } from 'lucide-react';

const styles = {
    container: {
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
    },
    card: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '24px',
    },
    cardHeader: {
        padding: '16px',
        borderBottom: '1px solid #eee',
    },
    cardTitle: {
        fontSize: '20px',
        fontWeight: '600',
    },
    cardContent: {
        padding: '16px',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '16px',
        marginBottom: '24px',
    },
    sectionTitle: {
        fontSize: '16px',
        fontWeight: '600',
        marginBottom: '8px',
    },
    monospace: {
        fontFamily: 'monospace',
    },
    profitText: {
        color: '#10B981',
    },
    button: {
        padding: '8px 16px',
        borderRadius: '4px',
        border: 'none',
        cursor: 'pointer',
    },
    primaryButton: {
        backgroundColor: '#3B82F6',
        color: 'white',
    },
    destructiveButton: {
        backgroundColor: '#EF4444',
        color: 'white',
    },
    input: {
        padding: '8px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        width: '200px',
        marginRight: '8px',
    },
    profitPositive: {
        color: '#10b981',
        fontWeight: '600',
    },
    profitNegative: {
        color: '#ef4444',
        fontWeight: '600',
    },
};

const TokenPositionPage = () => {
    const [searchParams] = useSearchParams();
    const [notification, setNotification] = useState(null);


    const showNotification = (type, title, message) => {
        setNotification({ type, title, message });
        setTimeout(() => setNotification(null), 5000);
    };


    // Access parameters
    const address = searchParams.get('quoteMint');
    const callerName = searchParams.get('callerName');
    const initialPrice = searchParams.get('initialPrice');
    const currentPrice = searchParams.get('currentPrice');
    const highestPrice = searchParams.get('highestPrice');
    const stopLossPrice = searchParams.get('stopLoss');
    const remainingPosition = searchParams.get('remaining');
    const profitLoss = searchParams.get('profitLoss');
    const levelsReached = (() => {
        try {
            const levelsStr = searchParams.get('levels');
            if (!levelsStr) return {};
            return JSON.parse(levelsStr);
        } catch (error) {
            console.error('Error parsing levels:', error);
            return {};
        }
    })();

    const [stopLoss, setStopLoss] = useState(stopLossPrice);

    const priceData = [
        { time: 'Entry', price: Number(initialPrice) },
        { time: 'Highest', price: Number(highestPrice) },
        { time: 'Current', price: Number(currentPrice) }
    ];

    const formatNumber = (num) => Number(num).toFixed(15);
    const formatPercentage = (num) => Number(num).toFixed(2);

    const handleClosePosition = async () => {
        try {
            const response = await fetch('https://solana-callers-bot-production.up.railway.app/api/positions/close', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, callerName, profitLoss })
            });
            const data = await response.json();

            if (response.ok) {
                showNotification('success', 'Success', data.message);
            } else {
                showNotification('error', 'Error', data.error);
            }
        } catch (error) {
            showNotification('error', 'Error', 'Failed to close position');
        }
    };

    const handleUpdateStopLoss = async () => {
        try {
            const response = await fetch('https://solana-callers-bot-production.up.railway.app/api/positions/stopLoss', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, stopLoss })
            });
            const data = await response.json();

            if (response.ok) {
                showNotification('success', 'Success', data.message);
            } else {
                showNotification('error', 'Error', data.error);
            }
        } catch (error) {
            showNotification('error', 'Error', 'Failed to update stop loss');
        }
    };

    return (
        <div style={styles.container}>

            {notification && (
                <div style={{
                    position: 'fixed',
                    top: '16px',
                    right: '16px',
                    width: '384px',
                    zIndex: 50
                }}>
                    <Alert style={{
                        backgroundColor: notification.type === 'success' ? '#f0fdf4' : '#fef2f2',
                        borderColor: notification.type === 'success' ? '#bbf7d0' : '#fecaca',
                        marginBottom: '8px'
                    }}>
                        <AlertTitle style={{
                            color: notification.type === 'success' ? '#166534' : '#991b1b'
                        }}>
                            {notification.title}
                        </AlertTitle>
                        <AlertDescription style={{
                            color: notification.type === 'success' ? '#15803d' : '#b91c1c'
                        }}>
                            {notification.message}
                        </AlertDescription>
                        <button
                            onClick={() => setNotification(null)}
                            style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px'
                            }}
                        >
                            <X style={{ height: '16px', width: '16px' }} />
                        </button>
                    </Alert>
                </div>
            )}
            <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <h2 style={styles.cardTitle}>Position Details</h2>
                </div>
                <div style={styles.cardContent}>
                    <div style={styles.grid}>
                        <div>
                            <h3 style={styles.sectionTitle}>Token Address</h3>
                            <p style={styles.monospace}>{address}</p>
                        </div>
                        <div>
                            <h3 style={styles.sectionTitle}>Caller Name</h3>
                            <p style={styles.monospace}>{callerName}</p>
                        </div>
                        <div>
                            <h3 style={styles.sectionTitle}>Current Price</h3>
                            <p>{formatNumber(currentPrice)} SOL</p>
                        </div>
                        <div>
                            <h3 style={styles.sectionTitle}>Entry Price</h3>
                            <p>{formatNumber(initialPrice)} SOL</p>
                        </div>
                        <div>
                            <h3 style={styles.sectionTitle}>Highest Price</h3>
                            <p>{formatNumber(highestPrice)} SOL</p>
                        </div>
                        <div>
                            <h3 style={styles.sectionTitle}>Stop Loss</h3>
                            <p>{formatNumber(stopLossPrice)} SOL</p>
                        </div>
                        <div>
                            <h3 style={styles.sectionTitle}>PnL</h3>
                            <p style={Number(profitLoss) >= 0 ? styles.profitPositive : styles.profitNegative}>
                                {formatPercentage(profitLoss)}%
                            </p>
                        </div>
                        <div>
                            <h3 style={styles.sectionTitle}>Remaining Position</h3>
                            <p>{remainingPosition}</p>
                        </div>
                        <div>
                            <h3 style={styles.sectionTitle}>Levels Reached</h3>
                            <p>{JSON.stringify(levelsReached)}</p>
                        </div>
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <h3 style={styles.sectionTitle}>Price Chart</h3>
                        <LineChart width={600} height={300} data={priceData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="time" />
                            <YAxis domain={['dataMin', 'dataMax']} />
                            <Tooltip />
                            <Line type="monotone" dataKey="price" stroke="#8884d8" />
                        </LineChart>
                    </div>

                    <div style={styles.grid}>
                        <div>
                            <h3 style={styles.sectionTitle}>Update Stop Loss</h3>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <input
                                    type="number"
                                    value={stopLoss}
                                    onChange={(e) => setStopLoss(e.target.value)}
                                    placeholder="New stop loss price"
                                    style={styles.input}
                                />
                                <button onClick={handleUpdateStopLoss} style={{ ...styles.button, ...styles.primaryButton }}>
                                    Update
                                </button>
                            </div>
                        </div>
                        <div>
                            <h3 style={styles.sectionTitle}>Close Position</h3>
                            <button onClick={handleClosePosition} style={{ ...styles.button, ...styles.destructiveButton }}>
                                Close Position
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TokenPositionPage;